import { useState, type FormEvent } from "react";

const SUBSCRIBE_ADDR = "https://readdy.ai/api/form/dagk2m1tto0ceqqnhthg";
const CONTACT_ADDR = "https://readdy.ai/api/form/dagk2m1tto0ceqqnhti0";

function submitForm(form: HTMLFormElement, url: string) {
  return fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams(new FormData(form) as never),
  });
}

function statusText(code: number, msg: string, detail: string) {
  if (code === 200 && !/spam/i.test(`${msg}${detail}`)) return "ok";
  return "error";
}

/* —— 每日瓜讯订阅 —— */
export function SubscribeForm() {
  const [state, setState] = useState<{ kind: "idle" | "ok" | "err"; msg: string }>({
    kind: "idle",
    msg: "",
  });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = new FormData(form).get("website_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      setState({ kind: "ok", msg: "订阅成功，最新热瓜即将送达你的邮箱。" });
      form.reset();
      return;
    }
    setLoading(true);
    try {
      const res = await submitForm(form, SUBSCRIBE_ADDR);
      const text = await res.text();
      let msg = "";
      let detail = "";
      try {
        const parsed = JSON.parse(text);
        msg = parsed?.meta?.message ?? parsed?.message ?? "";
        detail = parsed?.meta?.detail ?? "";
      } catch {
        /* 忽略非 JSON */
      }
      if (statusText(res.status, msg, detail) === "ok") {
        setState({ kind: "ok", msg: "订阅成功，最新热瓜即将送达你的邮箱。" });
        form.reset();
      } else {
        setState({
          kind: "err",
          msg: detail || msg || "提交失败，请稍后再试。",
        });
      }
    } catch {
      setState({ kind: "err", msg: "网络开小差了，请稍后再试。" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      id="form-newsletter"
      data-readdy-form
      onSubmit={onSubmit}
      className="w-full"
    >
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <i className="ri-mail-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
          <input
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="输入你的邮箱，接收每日瓜讯"
            className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-9 pr-3 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="c-btn bg-primary-500 px-5 text-background-50 hover:bg-primary-600 disabled:opacity-60"
        >
          {loading ? "提交中…" : "免费订阅"}
        </button>
      </div>
      {/* 蜜罐 */}
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />
      {state.kind !== "idle" && (
        <p
          className={`mt-3 flex items-start gap-2 rounded-md px-3 py-2 text-sm ${
            state.kind === "ok"
              ? "bg-primary-50 text-primary-700"
              : "bg-secondary-200 text-secondary-900"
          }`}
        >
          <i
            className={`mt-0.5 ${state.kind === "ok" ? "ri-checkbox-circle-line" : "ri-error-warning-line"}`}
          ></i>
          <span>{state.msg}</span>
        </p>
      )}
    </form>
  );
}

/* —— 投稿 / 联系 —— */
export function ContactForm() {
  const [state, setState] = useState<{ kind: "idle" | "ok" | "err"; msg: string }>({
    kind: "idle",
    msg: "",
  });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = fd.get("website_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      setState({ kind: "ok", msg: "信息已收到，我们会尽快与你联系。" });
      form.reset();
      return;
    }
    setLoading(true);
    try {
      const res = await submitForm(form, CONTACT_ADDR);
      const text = await res.text();
      let msg = "";
      let detail = "";
      try {
        const parsed = JSON.parse(text);
        msg = parsed?.meta?.message ?? parsed?.message ?? "";
        detail = parsed?.meta?.detail ?? "";
      } catch {
        /* 忽略非 JSON */
      }
      if (statusText(res.status, msg, detail) === "ok") {
        setState({ kind: "ok", msg: "信息已收到，我们会尽快与你联系。" });
        form.reset();
      } else {
        setState({
          kind: "err",
          msg: detail || msg || "提交失败，请稍后再试。",
        });
      }
    } catch {
      setState({ kind: "err", msg: "网络开小差了，请稍后再试。" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      id="form-contact"
      data-readdy-form
      onSubmit={onSubmit}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-foreground-700">
            称呼
          </span>
          <input
            type="text"
            name="name"
            placeholder="怎么称呼你"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-foreground-700">
            联系邮箱
          </span>
          <input
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="用于接收回复"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </label>
      </div>
      <label className="block">
        <span className="mb-1.5 block text-sm font-medium text-foreground-700">
          联系类型
        </span>
        <select
          name="subject"
          className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
        >
          <option value="投稿爆料">我要投稿爆料（圈内素材）</option>
          <option value="合作">品牌 / 内容合作</option>
          <option value="建议">意见建议</option>
          <option value="其他">其他</option>
        </select>
      </label>
      <label className="block">
        <span className="mb-1.5 block text-sm font-medium text-foreground-700">
          留言内容
        </span>
        <textarea
          name="message"
          required
          rows={5}
          maxLength={500}
          placeholder="请尽量描述清楚时间、人物关系与线索（500 字以内），涉及真实隐私的内容建议匿名化描述。"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        />
        <span className="mt-1 block text-right text-xs text-foreground-400">
          最多 500 字
        </span>
      </label>
      {/* 蜜罐 */}
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />
      <button
        type="submit"
        disabled={loading}
        className="c-btn w-full bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-60 sm:w-auto"
      >
        <i className="ri-send-plane-line mr-1.5"></i>
        {loading ? "发送中…" : "提交信息"}
      </button>
      {state.kind !== "idle" && (
        <p
          className={`flex items-start gap-2 rounded-md px-3 py-2 text-sm ${
            state.kind === "ok"
              ? "bg-primary-50 text-primary-700"
              : "bg-secondary-200 text-secondary-900"
          }`}
        >
          <i
            className={`mt-0.5 ${state.kind === "ok" ? "ri-checkbox-circle-line" : "ri-error-warning-line"}`}
          ></i>
          <span>{state.msg}</span>
        </p>
      )}
    </form>
  );
}